import type {CSSProperties} from 'react'
import {useEffect, useState} from 'react'
import {Icon} from 'src/common/Icon'

const TEXT_COLORS = [
  {value: '#131212', label: 'Black'},
  {value: '#595959', label: 'Gray'},
]

const normalizeColor = (raw: string): string | null => {
  if (!raw) {
    return null
  }

  if (raw.includes('19, 18, 18') || raw === '#131212') {
    return '#131212'
  }

  if (raw.includes('89, 89, 89') || raw === '#595959') {
    return '#595959'
  }

  return null
}

// Walk up from a node to find the nearest inline color style
const colorFromNode = (node: Node): string | null => {
  let el: HTMLElement | null = node.nodeType === 3 ? node.parentElement : (node as HTMLElement)

  while (el) {
    if (el.style?.color) {
      return normalizeColor(el.style.color)
    }

    el = el.parentElement
  }

  return null
}

// Returns the uniform palette color if all selected text shares one color, null if mixed.
const detectUniformColor = (): string | null => {
  const sel = window.getSelection()

  if (!sel || !sel.rangeCount) {
    return null
  }

  const range = sel.getRangeAt(0)
  const ancestor = range.commonAncestorContainer

  // Single text node — check its parent chain directly
  if (ancestor.nodeType === 3) {
    return colorFromNode(ancestor)
  }

  // Multi-node — walk all text nodes within the selection
  const walker = document.createTreeWalker(ancestor, NodeFilter.SHOW_TEXT)
  const colors = new Set<string | null>()

  let node = walker.nextNode()

  while (node) {
    if (range.intersectsNode(node)) {
      colors.add(colorFromNode(node))
    }

    node = walker.nextNode()
  }

  if (colors.size === 1) {
    return colors.values().next().value ?? null
  }

  return null
}

export function SelectionToolbar() {
  const [pos, setPos] = useState<{x: number; y: number} | null>(null)
  const [states, setStates] = useState({bold: false, italic: false, underline: false, list: false, highlight: false})
  const [textColor, setTextColor] = useState<string | null>(null)

  // Show/hide toolbar and sync formatting states on text selection change
  useEffect(() => {
    const check = () => {
      const sel = window.getSelection()

      if (!sel || sel.isCollapsed || !sel.rangeCount) {
        setPos(null)
        return
      }

      const range = sel.getRangeAt(0)
      let container = range.commonAncestorContainer

      if (container.nodeType === 3) {
        container = container.parentElement!
      }

      if (!container || !(container as Element).closest?.('[contenteditable]')) {
        setPos(null)
        return
      }

      const rect = range.getBoundingClientRect()

      if (rect.width < 2) {
        setPos(null)
        return
      }

      setPos({x: rect.left + rect.width / 2, y: rect.top - 10})

      let node: Node | null = range.commonAncestorContainer

      if (node.nodeType === 3) {
        node = node.parentElement
      }

      setStates({
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        underline: document.queryCommandState('underline'),
        list: document.queryCommandState('insertUnorderedList'),
        highlight: !!(node as Element | null)?.closest?.('mark'),
      })

      setTextColor(detectUniformColor())
    }

    document.addEventListener('selectionchange', check)
    return () => document.removeEventListener('selectionchange', check)
  }, [])

  const run = (cmd: string, e: React.MouseEvent) => {
    e.preventDefault()
    document.execCommand(cmd)

    setStates((s) => ({
      ...s,
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      list: document.queryCommandState('insertUnorderedList'),
    }))
  }

  const applyColor = (color: string, e: React.MouseEvent) => {
    e.preventDefault()
    const sel = window.getSelection()

    if (!sel || sel.isCollapsed || !sel.rangeCount) {
      return
    }

    const range = sel.getRangeAt(0)

    // Collect all text nodes that intersect the selection
    const textNodes: Text[] = []
    const ancestor = range.commonAncestorContainer

    if (ancestor.nodeType === 3) {
      // Single text node selected
      textNodes.push(ancestor as Text)
    } else {
      const walker = document.createTreeWalker(ancestor, NodeFilter.SHOW_TEXT)
      let node = walker.nextNode()

      while (node) {
        if (range.intersectsNode(node)) {
          textNodes.push(node as Text)
        }

        node = walker.nextNode()
      }
    }

    // Wrap each text node (or its selected portion) in a color span.
    // Applying at leaf level ensures the inline style beats CSS class selectors (e.g. [&_strong]).
    const wrappedSpans: HTMLSpanElement[] = []

    for (const textNode of textNodes) {
      const nodeRange = document.createRange()

      if (textNode === range.startContainer && textNode === range.endContainer) {
        nodeRange.setStart(textNode, range.startOffset)
        nodeRange.setEnd(textNode, range.endOffset)
      } else if (textNode === range.startContainer) {
        nodeRange.setStart(textNode, range.startOffset)
        nodeRange.setEndAfter(textNode)
      } else if (textNode === range.endContainer) {
        nodeRange.setStartBefore(textNode)
        nodeRange.setEnd(textNode, range.endOffset)
      } else {
        nodeRange.selectNode(textNode)
      }

      const span = document.createElement('span')
      span.style.color = color
      nodeRange.surroundContents(span)
      wrappedSpans.push(span)
    }

    // Restore selection across all wrapped spans so the toolbar stays visible
    if (wrappedSpans.length > 0) {
      const restored = document.createRange()
      restored.setStartBefore(wrappedSpans[0])
      restored.setEndAfter(wrappedSpans[wrappedSpans.length - 1])
      sel.removeAllRanges()
      sel.addRange(restored)
    }

    setTextColor(color)
  }

  const toggleHighlight = (e: React.MouseEvent) => {
    e.preventDefault()
    const sel = window.getSelection()

    if (!sel || sel.isCollapsed || !sel.rangeCount) {
      return
    }

    const range = sel.getRangeAt(0)
    let node: Node | null = range.commonAncestorContainer

    if (node.nodeType === 3) {
      node = node.parentElement
    }

    const existing = (node as Element | null)?.closest?.('mark')

    if (existing) {
      const parent = existing.parentNode!

      while (existing.firstChild) {
        parent.insertBefore(existing.firstChild, existing)
      }

      parent.removeChild(existing)
      setStates((s) => ({...s, highlight: false}))
      return
    }

    const mark = document.createElement('mark')

    try {
      range.surroundContents(mark)
      setStates((s) => ({...s, highlight: true}))
    } catch {
      // Invalid range (e.g. selection spans multiple elements) — ignore to avoid crashing the editor
    }
  }

  if (!pos) {
    return null
  }

  const btnStyle = (active: boolean): CSSProperties => ({
    background: active ? 'rgba(255,255,255,0.15)' : 'none',
    border: 'none',
    borderRadius: 4,
    cursor: 'pointer',
    padding: '5px 9px',
    display: 'flex',
    alignItems: 'center',
    color: '#fff',
    fontFamily: 'Georgia, serif',
    fontSize: 14,
    transition: 'background 120ms',
    lineHeight: 1,
  })

  return (
    <div
      data-epg-section
      className="fixed z-[9999] flex items-center gap-px rounded-lg bg-moss-black p-[3px_4px] shadow-[0_6px_20px_rgba(0,0,0,0.22)]"
      style={{
        left: pos.x,
        top: pos.y,
        transform: 'translate(-50%, -100%)',
        animation: 'epgToolbarIn 150ms cubic-bezier(0.25, 0, 0, 1) both',
      }}
    >
      <button onMouseDown={(e) => run('bold', e)} title="Bold" style={btnStyle(states.bold)}>
        <span style={{fontWeight: 800}}>B</span>
      </button>
      <button onMouseDown={(e) => run('italic', e)} title="Italic" style={btnStyle(states.italic)}>
        <span style={{fontStyle: 'italic'}}>I</span>
      </button>
      <button onMouseDown={(e) => run('underline', e)} title="Underline" style={btnStyle(states.underline)}>
        <span style={{textDecoration: 'underline'}}>U</span>
      </button>
      <div style={{width: 1, height: 16, background: 'rgba(255,255,255,0.15)', margin: '0 2px'}} />
      {TEXT_COLORS.map((c) => (
        <button
          key={c.value}
          title={c.label}
          onMouseDown={(e) => applyColor(c.value, e)}
          style={{...btnStyle(textColor === c.value), padding: '5px 6px'}}
        >
          <span
            style={{
              display: 'block',
              width: 12,
              height: 12,
              borderRadius: '50%',
              background: c.value,
              boxShadow: textColor === c.value ? '0 0 0 2px rgba(255,255,255,0.5)' : 'none',
              transition: 'box-shadow 120ms',
            }}
          />
        </button>
      ))}
      <div style={{width: 1, height: 16, background: 'rgba(255,255,255,0.15)', margin: '0 2px'}} />
      <button onMouseDown={toggleHighlight} title="Highlight" style={btnStyle(states.highlight)}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <rect x="1" y="9.5" width="12" height="2.5" rx="1" fill="rgba(180,255,68,0.7)" />
          <text
            x="7"
            y="9"
            textAnchor="middle"
            fontSize="8"
            fontWeight="700"
            fill="currentColor"
            fontFamily="Georgia, serif"
          >
            A
          </text>
        </svg>
      </button>
      <button onMouseDown={(e) => run('insertUnorderedList', e)} title="Bullet list" style={btnStyle(states.list)}>
        <Icon name="list" className="size-3.5" />
      </button>
    </div>
  )
}
